import configs from "../configs";
import userPreferences from "./UserPreferences";
import { preferencesForRemoteSync } from "./preferenceSync";
import EventEmitter from "eventemitter3";
import PublishDialog from "../ui/dialogs/PublishDialog";
import ProgressDialog from "../ui/dialogs/ProgressDialog";
import PerformanceCheckDialog from "../ui/dialogs/PerformanceCheckDialog";
import jwtDecode from "jwt-decode";
import { buildAbsoluteURL } from "url-toolkit";
import PublishedSceneDialog from "./PublishedSceneDialog";
import { RethrownError } from "../editor/utils/errors";
import { readableStreamToJson, readableStreamToPng } from "../editor/utils/convert-utils";
import FolderService from "./FolderService";
import SaveNewProjectDialog from "../ui/dialogs/SaveNewProjectDialog";
import ErrorDialog from "../ui/dialogs/ErrorDialog";
import { getAuth0Token, hasLoginWithRedirect, loginWithPopup, loginWithRedirect } from "../ui/utils";
import SubscriptionsService from "./SubscriptionsService";
import ScenesService from "./ScenesService";
import PersistableNode from "../editor/nodes/PersistableNode";
import { defaultThumbnail } from "../defaultScene";
import ProjectsService from "./ProjectsService";
import TemplatesService from "./TemplatesService";
import { uploadFile } from "../editor/utils/file";
import {
  beginApplicationBuild,
  beginProjectDraftWrite,
  cancelApplicationBuild,
  cancelProjectDraftWrite,
  completeApplicationBuild,
  completeProjectDraftWrite,
  findDelegatedAssetSlot,
  uploadDelegatedAsset
} from "./delegatedAssetWrite.js";
import EventService from "./EventService";
import SettingsService from "./SettingsService";
import SiteSettingsService from "./SiteSettingsService";
import PublishingCategoriesService from "./PublishingCategoriesService";
import AssetOptimizationService from "./AssetOptimizationService";
import { dashboardApiClient } from "./DashboardApiClient";
import {
  activateProductionDeploymentBuild,
  deleteProductionDeploymentBuild,
  listProductionDeploymentBuilds
} from "./productionDeploymentBuilds.js";
import { defaultRoomUrlForEnvironment } from "./publish-room-url.js";
import { reconcileProjectDefaultRooms } from "./project-default-rooms.js";
import { compress } from "../ui/utils/lz4";
import {
  buildPopupConfig,
  normalizePopupAuthError,
  safelyClosePopupFromError,
  createAuthPopupFailure
} from "../ui/auth/popupAuthPolicy";
import {
  isDashboardBridgeConfigured,
  loginWithDashboardBridgePopup,
  startDashboardBridgeRedirect
} from "../ui/auth/dashboardBridgeAuth.js";
import { setHttpOnlyCookie } from "../cookieUtils";
import { handleInstantAccessTokenExpiry } from "./instantAccessReauth";
import { handleWidgetReauthFailure } from "./widgetReauthRedirect";
import {
  LOCAL_STORE_KEY,
  persistLocalAuthSession,
  readLocalAuthSession,
  shouldEmitAuthenticationChanged
} from "./authSessionStore.js";
import { buildApplicationBuildPayload } from "./projectPublishPayload.js";
import { cacheRequest, getPersistenceApiUrl, normalizeResolvedMediaResponse } from "./mediaResolverUtils.js";
import { getTenantHostHeader } from "../utils/tenantHost.js";

// Media related functions should be kept up to date with Hubs media-utils:
// https://github.com/mozilla/hubs/blob/master/src/utils/media-utils.js

const resolveUrlCache = new Map();
const resolveMediaCache = new Map();

const nonCorsProxyDomains = (configs.NON_CORS_PROXY_DOMAINS || "").split(",");
if (configs.CORS_PROXY_SERVER) {
  nonCorsProxyDomains.push(configs.CORS_PROXY_SERVER);
}

function shouldCorsProxy(url) {
  try {
    const parsedUrl = new URL(url);

    if (nonCorsProxyDomains.find(domain => parsedUrl.hostname.endsWith(domain))) return false;
  } catch (e) {
    // Ignore
  }

  return true;
}

export const proxyUrlScripting = `https://${configs.CORS_PROXY_SERVER}`; // TODO: remove

export const proxyUrl =
  configs.ENVIRONMENT && configs.ENVIRONMENT == "local" ? `` : `https://${configs.CORS_PROXY_SERVER}`;

export const proxiedUrlFor = url => {
  if (!(url.startsWith("http:") || url.startsWith("https:")) || url.includes("sketchfab.com")) return url;

  if (!shouldCorsProxy(url)) {
    return url;
  }

  if (!configs.CORS_PROXY_SERVER) return url;

  return `https://${configs.CORS_PROXY_SERVER}/${url}`;
};

export function workspaceHasPermission(workspace, permission) {
  return (
    workspace?.authorization?.projection_status === "ready" &&
    Array.isArray(workspace.authorization.permissions) &&
    workspace.authorization.permissions.includes(permission)
  );
}

function isCanonicalWorkspacePermission(permission) {
  return /^(workspace|project|template|event|asset|verse|instant-access):/.test(permission);
}

function buildApplicationActivations(versionType) {
  const environments =
    versionType === "both"
      ? ["production", "development"]
      : [versionType === "production" ? "production" : "development"];
  return environments.map(environment_key => ({ environment_key, target_key: "room" }));
}

export const unproxiedUrlFor = url => {
  if (typeof url !== "string") {
    console.warn("Input is not a string, returning as is.");
    return url;
  }

  const proxyPattern = /^(https?:\/\/.+?)(https?:\/\/.*)$/;
  const match = url.match(proxyPattern);

  if (match && match[2]) {
    return match[2];
  } else {
    return url;
  }
};

const CommonKnownContentTypes = {
  gltf: "model/gltf",
  glb: "model/gltf-binary",
  png: "image/png",
  jpg: "image/jpeg",
  jpeg: "image/jpeg",
  pdf: "application/pdf",
  mp4: "video/mp4",
  mp3: "audio/mpeg"
};

function guessContentType(url) {
  const extension = new URL(url).pathname.split(".").pop();
  return CommonKnownContentTypes[extension];
}

export default class Project extends EventEmitter {
  constructor() {
    super();

    const { protocol, host } = new URL(window.location.href);

    this.serverURL = protocol + "//" + host;

    this.projectDirectoryPath = "/api/files/";

    // Max size in MB
    this.maxUploadSize = 128;

    const apiClient = {
      fetch: this.fetch.bind(this),
      getToken: this.getToken.bind(this),
      emit: this.emit.bind(this),
      getProject: this.getProject.bind(this),
      getProjectContentUrl: this.getProjectContentUrl.bind(this),
      cloneProject: this.cloneProject.bind(this),
      deleteProject: this.deleteProject.bind(this),
      getAuthUser: this.getAuthUser.bind(this),
      synchronizeProject: this.synchronizeProject.bind(this),
      cloneAssets: this.cloneAssets.bind(this)
    };

    this.folderService = new FolderService();
    this.authUser = null;
    this.subscriptionsService = new SubscriptionsService();
    this.scenesService = new ScenesService();
    this.projectsService = new ProjectsService(apiClient);
    this.templatesService = new TemplatesService(apiClient);
    this.eventService = new EventService(apiClient);
    this.settingsService = new SettingsService(apiClient);
    this.siteSettingsService = new SiteSettingsService();
    this.publishingCategoriesService = new PublishingCategoriesService();
    this.assetOptimizationService = new AssetOptimizationService();
    this._workspaceGetter = null; // Will be set by components with access to AuthContext
    this._userIdGetter = null; // Will be set by components with access to AuthContext
    this._projectScope = null;
  }

  /**
   * Set a function to get the current workspace object from context.
   * This should be called from components that have access to AuthContext.
   * @param {Function} getter - Function that returns the current workspace object
   * @param {Function} userIdGetter - Function that returns the current user ID
   */
  setWorkspaceGetter(getter, userIdGetter = null) {
    this._workspaceGetter = getter;
    this._userIdGetter = userIdGetter;
    // Also set workspace ID getter in ProjectsService (derived from workspace)
    if (this.projectsService) {
      this.projectsService.setWorkspaceIdGetter(() => this.getWorkspaceId());
    }
    if (dashboardApiClient) {
      dashboardApiClient.setWorkspaceIdGetter(() => this.getWorkspaceId());
    }
  }

  /**
   * @deprecated Use setWorkspaceGetter instead
   */
  setWorkspaceIdGetter(getter) {
    // Backward compatibility: wrap the ID getter to return a workspace-like object
    this._workspaceGetter = () => {
      const id = getter();
      return id ? { id } : null;
    };
    if (this.projectsService) {
      this.projectsService.setWorkspaceIdGetter(getter);
    }
    if (dashboardApiClient) {
      dashboardApiClient.setWorkspaceIdGetter(getter);
    }
  }

  getWorkspace() {
    if (this._workspaceGetter) {
      return this._workspaceGetter();
    }
    return null;
  }

  getWorkspaceId() {
    if (this._projectScope?.workspaceId) return this._projectScope.workspaceId;
    // Try to get workspace from context first (via injected getter)
    const workspace = this.getWorkspace();
    if (workspace?.id) {
      return workspace.id;
    }

    // Fallback to localStorage (for backward compatibility during transition)
    const workspaceId = localStorage.getItem("selected_workspace_id");
    if (!workspaceId) {
      throw new Error("No workspace selected. Please select a workspace first.");
    }
    return workspaceId;
  }

  setProjectScope(workspaceId, projectKey) {
    if (!workspaceId || !projectKey) throw new Error("Project workspace and key are required.");
    this._projectScope = { workspaceId, projectKey };
  }

  clearProjectScope() {
    this._projectScope = null;
  }

  /**
   * Get the current user's role in the current workspace
   * @returns {string|null} The role: "primaryOwner", "owner", "contributor", "viewer", or null
   */
  getWorkspaceRole() {
    const workspace = this.getWorkspace();
    return workspace?.authorization?.role || null;
  }

  /**
   * Whether the authenticated user owns the current Workspace.
   * Workspace ownership is an identity fact, not a role projection.
   */
  isCurrentWorkspaceOwner() {
    const workspace = this.getWorkspace();
    const currentUserId = this._userIdGetter ? this._userIdGetter() : null;

    if (workspace?.owner_identity_user_id == null || currentUserId == null) {
      return false;
    }

    return String(workspace.owner_identity_user_id) === String(currentUserId);
  }

  // getAuthContainer() {
  //   return AuthContainer;
  // }

  async authenticate(email, signal, token) {
    if (signal?.aborted) {
      const error = new Error("Authentication aborted");
      error.name = "AbortError";
      throw error;
    }

    if (!token) {
      throw new Error("Missing authentication token");
    }

    const previousStore = readLocalAuthSession(localStorage);
    persistLocalAuthSession(localStorage, { email, token });

    if (shouldEmitAuthenticationChanged(previousStore, { email, token })) {
      this.emit("authentication-changed", true);
    }
  }

  async reauthenticate({ withPopup = true, authorizationParams } = {}) {
    // Try to get token silently first (without popup)
    let auth0token = await getAuth0Token().catch(() => null);
    const clearAuthCookie = () => setHttpOnlyCookie("auth0token", "", -1);

    // If we don't have a valid token and popup is requested, try popup
    if (!auth0token && withPopup) {
      const didRedirectInstantAccess = handleInstantAccessTokenExpiry({
        storage: localStorage,
        location: window.location,
        routerBasePath: configs.ROUTER_BASE_PATH,
        clearAuthCookie,
        xrhubUrl: configs.XRHUB_URL
      });

      if (didRedirectInstantAccess) {
        return false;
      }

      try {
        if (isDashboardBridgeConfigured()) {
          auth0token = await loginWithDashboardBridgePopup(window.location.href);
        } else {
          const popupOptions = authorizationParams ? { authorizationParams } : {};
          await loginWithPopup({
            ...popupOptions,
            popupConfig: buildPopupConfig()
          });
          auth0token = await getAuth0Token().catch(() => null);
        }
      } catch (popupError) {
        const normalizedError = normalizePopupAuthError(popupError);
        if (
          isDashboardBridgeConfigured() &&
          (normalizedError.code === "auth_popup_timeout" || normalizedError.code === "auth_popup_blocked")
        ) {
          safelyClosePopupFromError(normalizedError);
          await startDashboardBridgeRedirect(window.location.href);
          return false;
        }

        const didRedirectWidgetUser = await handleWidgetReauthFailure({
          storage: localStorage,
          location: window.location,
          creatorBasePath: configs.ROUTER_BASE_PATH,
          auth0Redirect: configs.AUTH0_REDIRECT,
          clearAuthCookie,
          xrhubUrl: configs.XRHUB_URL,
          useLocalLogin: configs.isLocal,
          loginWithRedirect: hasLoginWithRedirect() ? loginWithRedirect : null
        });

        if (normalizedError.code === "auth_popup_timeout" || normalizedError.code === "auth_popup_blocked") {
          safelyClosePopupFromError(normalizedError);
          if (didRedirectWidgetUser) {
            return false;
          }
          throw createAuthPopupFailure(normalizedError);
        } else {
          if (didRedirectWidgetUser) {
            return false;
          }
          return false;
        }
      }

      if (!auth0token) {
        await handleWidgetReauthFailure({
          storage: localStorage,
          location: window.location,
          creatorBasePath: configs.ROUTER_BASE_PATH,
          auth0Redirect: configs.AUTH0_REDIRECT,
          clearAuthCookie,
          xrhubUrl: configs.XRHUB_URL,
          useLocalLogin: configs.isLocal,
          loginWithRedirect: hasLoginWithRedirect() ? loginWithRedirect : null
        });
        return false;
      }
    }

    // Only treat reauth as successful when we have a usable Auth0 token.
    if (!auth0token) {
      return false;
    }

    const auth0User = await this.getAuth0User(auth0token);
    const email = auth0User?.email;
    if (!email) {
      return false;
    }

    const abortController = new AbortController();
    await this.authenticate(email, abortController.signal, auth0token);
    return true;
  }

  async withPhoenixAuth(operation) {
    try {
      return await operation();
    } catch (error) {
      console.log("error from withPhoenixAuth", error);

      const status = error.status || error.originalError?.status || error.response?.status;

      // Handle banned user error (403)
      if (status === 403) {
        const errorMessage = error.message || "Your account has been suspended";
        // Emit event for account suspended
        this.emit("account-suspended", { message: errorMessage });
        throw error; // Re-throw to allow component handling
      }

      if (status === 401) {
        try {
          // Try silent reauthentication first
          await this.reauthenticate({ withPopup: false });
          return await operation();
        } catch (_) {
          // ignore and try popup next
        }

        // If silent auth failed, try with popup
        await this.reauthenticate({ withPopup: true });
        return await operation();
      }
      throw error;
    }
  }

  isAuthenticated() {
    const store = readLocalAuthSession(localStorage);

    return !!(store && store.credentials && store.credentials.token);
  }

  getToken() {
    const store = readLocalAuthSession(localStorage);

    if (!store || !store.credentials || !store.credentials.token) {
      throw new Error("Not authenticated");
    }

    return store.credentials.token;
  }

  getStore() {
    return readLocalAuthSession(localStorage);
  }

  async getAuth0User(token, forceUpdate = false) {
    const url = configs["CUSTOM_API_URL"] + "/auth0/user";
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      }).catch(() => {
        return null;
      });

      if (!response.ok) {
        // Handle banned user error (403)
        if (response.status === 403) {
          const errorData = await response.json().catch(() => ({}));
          const error = new Error(errorData.message || "Your account has been suspended");
          error.status = 403;
          error.code = "ACCOUNT_SUSPENDED";
          throw error;
        }
        console.error("Failed to fetch Auth0 user data:", response.status, response.statusText);
        return null;
      }

      const data = await response.json();
      if (!this.authUser) this.authUser = data;

      const store = JSON.parse(localStorage.getItem(LOCAL_STORE_KEY)) || {};

      // Check if permissions or roles have changed, or if forcing an update
      const permissionsChanged = JSON.stringify(store.authUser?.permissions) !== JSON.stringify(data.permissions);
      const rolesChanged = JSON.stringify(store.authUser?.roles) !== JSON.stringify(data.roles);

      if (!store.authUser || !Object.keys(store.authUser).length || permissionsChanged || rolesChanged || forceUpdate) {
        store.authUser = data;
        localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(store));

        // Emit event when permissions or roles change
        if ((permissionsChanged || rolesChanged) && store.authUser) {
          console.log("User permissions or roles changed, emitting authentication-changed event");
          this.emit("authentication-changed", true);
        }
      }

      return data;
    } catch (err) {
      console.error("Error fetching Auth0 user:", err);
      // Re-throw account suspended errors so they can be handled by the caller
      if (err.code === "ACCOUNT_SUSPENDED" || err.status === 403) {
        throw err;
      }
      return null;
    }
  }

  async getUserGroups() {
    const token = await getAuth0Token();
    const url = configs["CUSTOM_API_URL"] + "/user/groups";
    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      }).catch(() => {
        return null;
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      return data.data;
    } catch (err) {
      console.error(err);
      return [];
    }
  }

  async getOwnedUserScene(auth0token, metaverseData) {
    const url = configs["CUSTOM_API_URL"] + "/user/owned-metaverse-scenes";
    const body = {
      token: metaverseData.token,
      userId: metaverseData.user_id
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth0token}`,
          Accept: "application/json"
        }
      }).catch(() => {
        return null;
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      return data;
    } catch (err) {
      return null;
    }
  }

  async mapSceneUsersGroup(token, project, groups) {
    const body = {
      user_group_ids: groups.map(group => group.id),
      group_privacy: "private"
    };
    const url = configs["CUSTOM_API_URL"] + `/metaverse-scenes/${project.scene.scene_id}/user-groups`;
    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      }).catch(() => {
        return null;
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      return data;
    } catch (err) {
      return null;
    }
  }

  async findSceneSetUser(token, project, user) {
    const body = {
      user_id: user.id
    };
    const url = configs["CUSTOM_API_URL"] + `/metaverse-scenes/${project.scene.scene_id}`;
    try {
      const response = await fetch(url, {
        method: "PUT",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      }).catch(() => {
        return null;
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      return data;
    } catch (err) {
      return null;
    }
  }

  getAccountId() {
    const token = this.getToken();
    return jwtDecode(token).sub;
  }

  logout() {
    const storeItem = localStorage.getItem(LOCAL_STORE_KEY);
    if (storeItem) {
      const store = JSON.parse(storeItem);
      store.credentials = { email: "", token: "" };
      localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify(store));
    }
    window.location.reload();
    this.emit("authentication-changed", false);
  }

  showLoginDialog() {
    return this.loginWithPopup();
  }

  async getProjects() {
    return this.withPhoenixAuth(async () => this.projectsService.getProjects());
  }

  async getProject(projectKey, workspaceId = null) {
    try {
      if (workspaceId) this.setProjectScope(workspaceId, projectKey);
      const json = await dashboardApiClient.getJson(`projects/${projectKey}`);
      return json.project || json;
    } catch (error) {
      console.error("Error fetching project:", error);
      throw error;
    }
  }

  async resolveUrl(url, index) {
    if (!shouldCorsProxy(url)) {
      return { origin: url };
    }

    const cacheKey = `${url}|${index}`;
    if (resolveUrlCache.has(cacheKey)) return resolveUrlCache.get(cacheKey);

    const request = this.fetch(`${getPersistenceApiUrl(configs)}/media/resolve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ media: { url, index } })
    })
      .then(response => response.json())
      .then(result => normalizeResolvedMediaResponse(result, window.location.protocol));

    return cacheRequest(resolveUrlCache, cacheKey, request);
  }

  fetchContentType(accessibleUrl) {
    return this.fetch(accessibleUrl, { method: "HEAD" }).then(r => r.headers.get("content-type"));
  }

  async getContentType(url) {
    const result = await this.resolveUrl(url);
    const canonicalUrl = result.origin;
    const accessibleUrl = proxiedUrlFor(canonicalUrl);

    return (
      (result.meta && result.meta.expected_content_type) ||
      guessContentType(canonicalUrl) ||
      (await this.fetchContentType(accessibleUrl))
    );
  }

  async resolveMedia(url, index) {
    const absoluteUrl = new URL(url, window.location).href;

    if (absoluteUrl.startsWith(this.serverURL)) {
      return { accessibleUrl: absoluteUrl };
    }

    const cacheKey = `${absoluteUrl}|${index}`;

    if (resolveMediaCache.has(cacheKey)) return resolveMediaCache.get(cacheKey);

    const request = (async () => {
      let contentType, canonicalUrl, accessibleUrl, meta;

      try {
        if (absoluteUrl.includes("sketchfab.com/models/") || absoluteUrl.includes("sketchfab.com/3d-models/")) {
          const persistenceApiUrl = getPersistenceApiUrl(configs);
          const resolveEndpoint = `${persistenceApiUrl}/integrations/sketchfab/resolve?url=${encodeURIComponent(
            absoluteUrl
          )}`;

          const response = await fetch(resolveEndpoint);
          if (!response.ok) {
            throw new Error(`Persistence API Sketchfab resolution failed: ${response.statusText}`);
          }

          const json = await response.json();
          canonicalUrl = absoluteUrl;
          accessibleUrl = proxiedUrlFor(json.file_url);
          contentType = "model/gltf+zip";
          meta = { expected_content_type: contentType };
        } else {
          const result = await this.resolveUrl(absoluteUrl);
          canonicalUrl = result.origin;
          meta = result.meta;
          accessibleUrl = proxiedUrlFor(canonicalUrl);

          contentType =
            (result.meta && result.meta.expected_content_type) ||
            guessContentType(canonicalUrl) ||
            (await this.fetchContentType(accessibleUrl));
        }
      } catch (error) {
        throw new RethrownError(`Error resolving media "${absoluteUrl}"`, error);
      }

      try {
        if (contentType === "model/gltf+zip") {
          // TODO: Sketchfab object urls should be revoked after they are loaded by the glTF loader.
          const { getFilesFromSketchfabZip } = await import(
            /* webpackChunkName: "SketchfabZipLoader", webpackPrefetch: true */ "./SketchfabZipLoader"
          );
          const files = await getFilesFromSketchfabZip(accessibleUrl);
          return {
            canonicalUrl,
            accessibleUrl: files["scene.gtlf"].url,
            contentType,
            files
          };
        }
      } catch (error) {
        throw new RethrownError(`Error loading Sketchfab model "${accessibleUrl}"`, error);
      }

      return { canonicalUrl, accessibleUrl, contentType, meta };
    })();

    return cacheRequest(resolveMediaCache, cacheKey, request);
  }

  proxyUrl(url) {
    return proxiedUrlFor(url);
  }

  unproxyUrl(baseUrl, url) {
    if (configs.CORS_PROXY_SERVER) {
      const corsProxyPrefix = `https://${configs.CORS_PROXY_SERVER}/`;

      if (baseUrl.startsWith(corsProxyPrefix)) {
        baseUrl = baseUrl.substring(corsProxyPrefix.length);
      }

      if (url.startsWith(corsProxyPrefix)) {
        url = url.substring(corsProxyPrefix.length);
      }
    }

    // HACK HLS.js resolves relative urls internally, but our CORS proxying screws it up. Resolve relative to the original unproxied url.
    // TODO extend HLS.js to allow overriding of its internal resolving instead
    if (!url.startsWith("http")) {
      url = buildAbsoluteURL(baseUrl, url.startsWith("/") ? url : `/${url}`);
    }

    return proxiedUrlFor(url);
  }

  async cloneProjectFromBlankTemplate({ project_id: _project_id, project_url, name }) {
    return this.withPhoenixAuth(async () => {
      const { body: thumbnailStream } = await fetch(defaultThumbnail());
      const thumbnailBlob = await readableStreamToPng(thumbnailStream);
      const thumbnailFile = new File([thumbnailBlob], "thumbnail.png", { type: "image/png" });

      const appName = configs.APP_NAME || configs.REACT_APP_NAME || "onland";
      const folder = `assets360/${appName}/projects`;

      const thumbnailEntry = await uploadFile({
        file: thumbnailFile,
        folder,
        asset_type: "project",
        useWorkspaceAssets: true
      });

      const { body: readableStream } = await fetch(project_url);
      const seralizedScene = await readableStreamToJson(readableStream);

      const projectBlob = new Blob([JSON.stringify(seralizedScene)], {
        type: "application/json"
      });
      const projectFile = new File([projectBlob], "scene.mud", { type: "application/json" });

      const projectEntry = await uploadFile({
        file: projectFile,
        folder,
        asset_type: "project",
        useWorkspaceAssets: true
      });

      const payload = {
        name: name,
        screenshot_resource_id: thumbnailEntry.uuid,
        model_resource_id: projectEntry.uuid
      };

      const json = await dashboardApiClient.postJson("projects", payload);

      this.emit("project-saved");

      return json.project || json.data || json;
    });
  }

  async cloneProjectFromTemplate({ project_id, project_url, name, thumbnail_url }) {
    return this.withPhoenixAuth(async () => {
      try {
        const json = await dashboardApiClient.postJson(`projects/${project_id}/clone`, {
          project_url: project_url,
          name: name,
          thumbnail_url: thumbnail_url
        });

        this.emit("project-saved");

        // API returns { project: {...} }, extract the project data
        return json.project || json.data || json;
      } catch (error) {
        const status = error.status || error.response?.status || (error.message.includes("402") ? 402 : null);
        if (status === 402) {
          // this.emit("402-error");
        }
        throw error;
      }
    });
  }

  async cloneAssets(project_id, target_project_id, project_url) {
    try {
      const cloneAssetsUrl = configs.PERSISTENCE_API_URL + "/projects/" + project_id + "/clone";
      const cloneAssetsResp = await this.fetch(cloneAssetsUrl, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: `Bearer ${await getAuth0Token()}`
        },
        body: JSON.stringify({
          target_project_id: target_project_id,
          project_url: project_url
        })
      });

      const cloneAssetsJson = await cloneAssetsResp.json();

      return cloneAssetsJson;
    } catch (error) {
      console.error("Error cloning assets", error);

      const status = error.status || error.originalError?.status || error.response?.status;

      if (status === 402) {
        // this.emit("402-error");
      }
      throw error;
    }
  }

  async checkProjectCreationLimit() {
    return this.withPhoenixAuth(async () => {
      try {
        // Call the specific endpoint to check if the user can create a project in this workspace
        // DashboardApiClient automatically adds /workspaces/{workspaceId}/ prefix
        await dashboardApiClient.postJson("can-create-project", {});
        return true;
      } catch (error) {
        const status = error.status || error.response?.status;
        if (status === 402) {
          throw error;
        }
        // Rethrow other errors to handle them appropriately if needed, or return true if it's safe to proceed
        // For now, consistent with previous logic, we assume non-402 errors might not be limit blocks
        // But for a specific "can-create" endpoint, any error likely means "cannot create".
        // However, sticking to the requested 402 check logic:
        if (status === 403) {
          throw error; // Permission denied
        }
        return true;
      }
    });
  }

  async checkResourceCreationLimit() {
    return true;
  }

  async cloneProject({ project_id, project_url, name, thumbnail_url, options = {}, source_branch_id = null }) {
    return this.withPhoenixAuth(async () => {
      try {
        const json = await dashboardApiClient.postJson(`projects/${project_id}/clone`, {
          project_url: project_url,
          name: name || options.name,
          thumbnail_url: thumbnail_url,
          source_branch_id,
          options: options
        });

        this.emit("project-saved");

        // API returns { project: {...} }, extract the project data
        return json.project || json.data || json;
      } catch (error) {
        // Check if error message contains 402 status
        const statusMatch = error.message.match(/402/);
        const status = statusMatch ? 402 : error.status || error.originalError?.status || error.response?.status;
        if (status === 402) {
          // this.emit("402-error");
        }
        throw error;
      }
    });
  }

  async renameProject(oldProject, name) {
    const payload = {
      name
    };

    const json = await dashboardApiClient.patchJson(`projects/${oldProject.project_id}`, payload);

    // API returns { project: {...} }, extract the project data
    return json.project || json.data || json;
  }

  async updateProjectScreenshot(projectId, screenshotResourceId) {
    const payload = {
      screenshot_resource_id: screenshotResourceId
    };

    const json = await dashboardApiClient.patchJson(`projects/${projectId}`, payload);

    return json.project || json.data || json;
  }

  async createProject(scene, parentSceneId, thumbnailBlob, signal) {
    return this.withPhoenixAuth(async () => {
      this.emit("project-saving");

      // Ensure the user is authenticated before continuing.
      if (!this.isAuthenticated()) {
        await this.loginWithPopup();
      }

      if (signal.aborted) {
        throw new Error("Save project aborted");
      }

      const thumbnailFile = new File([thumbnailBlob], "thumbnail.png", { type: "image/png" });

      const appName = configs.APP_NAME || configs.REACT_APP_NAME || "onland";
      const folder = `assets360/${appName}/projects`;

      const thumbnailEntry = await uploadFile({
        file: thumbnailFile,
        folder,
        asset_type: "project",
        abortSignal: signal,
        useWorkspaceAssets: true
      });

      if (signal.aborted) {
        throw new Error("Save project aborted");
      }

      const serializedScene = scene.serialize();
      const projectBlob = new Blob([JSON.stringify(serializedScene)], {
        type: "application/json"
      });

      const projectFile = new File([projectBlob], "scene.mud", { type: "application/json" });

      const projectEntry = await uploadFile({
        file: projectFile,
        folder,
        asset_type: "project",
        abortSignal: signal,
        useWorkspaceAssets: true
      });

      if (signal.aborted) {
        throw new Error("Save project aborted");
      }

      const payload = {
        name: scene.name,
        screenshot_resource_id: thumbnailEntry.uuid,
        model_resource_id: projectEntry.uuid
      };

      if (parentSceneId) {
        payload.parent_scene_id = parentSceneId;
      }

      // Use request method directly to support signal abort
      const resp = await dashboardApiClient.request("projects", {
        method: "POST",
        body: JSON.stringify(payload),
        signal
      });

      if (signal.aborted) {
        throw new Error("Save project aborted");
      }

      const json = await dashboardApiClient.handleResponse(resp);

      this.emit("project-saved");

      // API returns { project: {...} }, extract the project data
      return json.project || json.data || json;
    });
  }

  async deleteProject(projectId) {
    return this.withPhoenixAuth(async () => {
      try {
        const json = await dashboardApiClient.deleteJson(`projects/${projectId}`);
        return json;
      } catch (error) {
        // Check if error message contains 401 status
        const statusMatch = error.message.match(/401/);
        const status = statusMatch ? 401 : error.status || error.response?.status;
        if (status === 401) {
          throw new Error("Not authenticated");
        }
        console.error("Error deleting project:", error);
        throw error;
      }
    });
  }

  async saveProject(projectId, editor, signal, opts = {}) {
    if (!this.isAuthenticated()) {
      await this.loginWithPopup();
    }

    if (signal.aborted) {
      throw new Error("Save project aborted");
    }

    const serializedScene = editor.scene.serialize({
      name: editor.scene.name
    });

    const compressedBlob = await compress(serializedScene);

    let screenshotFile = null;
    try {
      const thumbnailBlob = await editor.takeScreenshot(512, 320);
      screenshotFile = new File([thumbnailBlob], "thumbnail.png", { type: "image/png" });
    } catch (error) {
      console.warn("Failed to capture branch thumbnail during save:", error);
    }

    const operationKey = `project-draft-write-${crypto.randomUUID()}`;
    let receipt = null;
    let resp;
    let screenshotResourceId = null;

    try {
      receipt = await beginProjectDraftWrite({
        dashboardApiClient,
        projectKey: projectId,
        message: opts.message || "Checkpoint",
        includeScreenshot: Boolean(screenshotFile),
        idempotencyKey: operationKey,
        signal
      });
      const appName = configs.APP_NAME || configs.REACT_APP_NAME || "onland";
      const folder = `assets360/${appName}/projects`;

      if (screenshotFile) {
        screenshotResourceId = await uploadDelegatedAsset({
          uploadFile,
          file: screenshotFile,
          folder,
          assetType: "project",
          slot: findDelegatedAssetSlot(receipt, "project_screenshot"),
          signal
        });
      }

      const contentResourceId = await uploadDelegatedAsset({
        uploadFile,
        file: new File([compressedBlob], "scene.mud", { type: "application/x-mud" }),
        folder,
        assetType: "project",
        slot: findDelegatedAssetSlot(receipt, "project_content"),
        signal
      });
      resp = await completeProjectDraftWrite({
        dashboardApiClient,
        projectKey: projectId,
        operationId: receipt.operation_id,
        contentResourceId,
        screenshotResourceId: screenshotResourceId || undefined,
        projectName: editor.scene.name,
        idempotencyKey: `project-draft-write-complete-${crypto.randomUUID()}`,
        signal
      });
    } catch (error) {
      if (receipt?.operation_id) {
        try {
          await cancelProjectDraftWrite({
            dashboardApiClient,
            projectKey: projectId,
            operationId: receipt.operation_id
          });
        } catch (cancelError) {
          console.warn("Failed to cancel Project draft write after save failure:", cancelError);
        }
      }
      throw error;
    }

    this.emit("project-saved");

    const userId = opts.userId;
    const lastPublishedAt = opts.lastPublishedAt;
    const authUser = this.getAuthUser();

    if (screenshotResourceId && userId && authUser && userId === authUser.id && !lastPublishedAt) {
      // Keep the project-level thumbnail in sync for owner saves.
      (async () => {
        try {
          await this.updateProjectScreenshot(projectId, screenshotResourceId);
        } catch (error) {
          console.warn("Failed to update project thumbnail during save:", error);
        }
      })();
    }

    return resp;
  }

  async getProjectBranches(projectId) {
    const json = await dashboardApiClient.getJson(`projects/${projectId}/branches`);
    const branches = json.data || json;
    return Array.isArray(branches)
      ? branches.map(branch => ({
          ...branch,
          last_updated: branch.last_updated || branch.updated_at || null
        }))
      : branches;
  }

  async getProjectProductionBuilds(projectKey, availability = "actionable", page = 1, perPage = 100, signal) {
    return listProductionDeploymentBuilds({
      client: dashboardApiClient,
      projectKey,
      availability,
      page,
      perPage,
      signal
    });
  }

  async activateProjectProductionBuild(projectKey, buildId, signal) {
    return activateProductionDeploymentBuild({
      client: dashboardApiClient,
      projectKey,
      buildId,
      idempotencyKey: `production-build-activate-${crypto.randomUUID()}`,
      signal
    });
  }

  async deleteProjectProductionBuild(projectKey, buildId) {
    return deleteProductionDeploymentBuild({
      client: dashboardApiClient,
      projectKey,
      buildId
    });
  }

  async getProjectBranchCommits(projectId, branchId) {
    const json = await dashboardApiClient.getJson(`projects/${projectId}/branches/${branchId}/versions`);
    const versions = json.data || json;
    return Array.isArray(versions)
      ? versions.map(version => ({
          ...version,
          timestamp: version.timestamp || version.created_at || null,
          status: version.review_status || "draft"
        }))
      : versions;
  }

  async updateCommit(projectId, branchId, commitId, data) {
    const payload = { ...data };
    if (Object.prototype.hasOwnProperty.call(payload, "status")) {
      payload.review_status = payload.status;
      delete payload.status;
    }
    const resp = await dashboardApiClient.request(`projects/${projectId}/branches/${branchId}/versions/${commitId}`, {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(payload)
    });
    return dashboardApiClient.handleResponse(resp);
  }

  async mergeBranch(projectId, sourceBranchId, targetBranchId = "main", commitId = null) {
    const target = (await this.getProjectBranches(projectId)).find(branch => branch.id === targetBranchId);
    if (!target?.id || !target.head_version_id || !commitId)
      throw new Error("Merge requires immutable source and target versions.");
    const resp = await dashboardApiClient.request(`projects/${projectId}/branches/${target.id}/merge`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Idempotency-Key": `project-merge-${crypto.randomUUID()}`
      },
      body: JSON.stringify({
        source_branch_id: sourceBranchId,
        source_version_id: commitId,
        expected_target_head_version_id: target.head_version_id
      })
    });
    return dashboardApiClient.handleResponse(resp);
  }

  async checkoutCommit(projectId, sourceBranchId, commitId) {
    const resp = await dashboardApiClient.request(
      `projects/${projectId}/branches/${sourceBranchId}/versions/${commitId}/restore`,
      {
        method: "POST",
        headers: {
          "Idempotency-Key": `project-restore-${crypto.randomUUID()}`
        }
      }
    );
    return dashboardApiClient.handleResponse(resp);
  }

  async deleteCommit(projectId, branchId, commitId) {
    const resp = await dashboardApiClient.request(`projects/${projectId}/branches/${branchId}/versions/${commitId}`, {
      method: "DELETE",
      headers: {
        "Idempotency-Key": `project-version-delete-${crypto.randomUUID()}`
      }
    });
    return dashboardApiClient.handleResponse(resp);
  }

  async createCommitComment(projectId, _branchId, commitId, comment) {
    const resp = await dashboardApiClient.request(`projects/${projectId}/versions/${commitId}/comments`, {
      method: "POST",
      body: JSON.stringify({ body: comment }),
      headers: {
        "Content-Type": "application/json"
      }
    });
    return dashboardApiClient.handleResponse(resp);
  }

  async updateCommitComment(projectId, _branchId, commitId, commentId, comment) {
    const resp = await dashboardApiClient.request(`projects/${projectId}/versions/${commitId}/comments/${commentId}`, {
      method: "PATCH",
      body: JSON.stringify({ body: comment }),
      headers: {
        "Content-Type": "application/json"
      }
    });
    return dashboardApiClient.handleResponse(resp);
  }

  async getProjectMetadata(url) {
    const response = await this.fetch(url);
    const json = await response.json();
    return json;
  }

  async getProjectContentUrl(projectId, branchId = null, commitId = null) {
    const query = new URLSearchParams();
    if (branchId) query.set("branch_id", branchId);
    if (commitId) query.set("commit_id", commitId);
    const path = `projects/${projectId}/content${query.size ? `?${query}` : ""}`;
    const json = await dashboardApiClient.getJson(path);
    return {
      url: proxiedUrlFor(json.content_url),
      token: json.expires_at,
      metadataUrl: null,
      branchId: json.branch_id || null,
      commitId: json.commit_id || null
    };
  }

  async generateArtifacts(projectId, editor, signal, opts = {}) {
    return this.withPhoenixAuth(async () => {
      try {
        this.emit("project-saving");

        // Ensure the user is authenticated before continuing.
        if (!this.isAuthenticated()) {
          await this.loginWithPopup();
        }

        if (signal.aborted) {
          throw new Error("Save project aborted");
        }

        const thumbnailBlob = await editor.takeScreenshot(512, 360);

        if (signal.aborted) {
          throw new Error("Save project aborted");
        }

        const thumbnailFile = new File([thumbnailBlob], "thumbnail.png", { type: "image/png" });

        const appName = configs.APP_NAME || configs.REACT_APP_NAME || "onland";
        const folder = `assets360/${appName}/projects`;

        const thumbnailEntry = await uploadFile({
          file: thumbnailFile,
          folder,
          asset_type: "project",
          abortSignal: signal,
          useWorkspaceAssets: true
        });

        if (signal.aborted) {
          throw new Error("Save project aborted");
        }

        const serializedScene = editor.scene.serialize({
          name: editor.scene.name
        });
        const projectBlob = new Blob([JSON.stringify(serializedScene)], {
          type: "application/json"
        });
        const projectFile = new File([projectBlob], "scene.mud", { type: "application/json" });

        const projectEntry = await uploadFile({
          file: projectFile,
          folder,
          asset_type: "project",
          abortSignal: signal,
          useWorkspaceAssets: true
        });

        if (signal.aborted) {
          throw new Error("Save project aborted");
        }

        const project = {
          name: opts?.projectName || editor.scene.name,
          screenshot_resource_id: thumbnailEntry.uuid,
          model_resource_id: projectEntry.uuid
        };

        const sceneId = editor.scene.metadata && editor.scene.metadata.sceneId ? editor.scene.metadata.sceneId : null;

        if (sceneId) {
          project.scene_id = sceneId;
        }

        const payload = {
          name: project.name,
          screenshot_resource_id: project.screenshot_resource_id,
          model_resource_id: project.model_resource_id,
          project_id: projectId
        };

        if (project.scene_id) {
          payload.scene_id = project.scene_id;
        }

        return payload;
      } catch (error) {
        if (error.status === 402) {
          editor.handle402Error("storage");
          error.showedDialog = true;
        }
        throw error;
      }
    });
  }
  async healthCheck(project, editor, showDialog, hideDialog) {
    const _scene = editor.scene;

    const abortController = new AbortController();
    const signal = abortController.signal;
    try {
      showDialog(ProgressDialog, {
        title: "Health Check",
        message:
          "We're evaluating your scene to ensure it's optimized for deployment. After the check, you'll receive a detailed report with findings and suggestions for improvement.",
        cancelable: true,
        onCancel: () => abortController.abort()
      });

      const { glbBlob: _glbBlob, scores } = await editor.exportScene(abortController.signal, { scores: true });

      if (signal.aborted) {
        const error = new Error("Publish project aborted");
        error.aborted = true;
        throw error;
      }

      showDialog(PerformanceCheckDialog, {
        scores,
        cancellable: false,
        onCancel: null,
        confirmLabel: "Continue",
        onConfirm: hideDialog
      });
    } catch (error) {
      showDialog(ErrorDialog, {
        title: "Health Check Stopped",
        // message: error.message || "There was an unknown error.",
        message:
          "The health check process was abruptly stopped. If you intentionally canceled the check, you can now continue editing your scene. If this was unexpected, please run the health check again to ensure everything is properly evaluated.",
        error,
        confirmLabel: "Understood"
      });
    }
  }

  async publishProject(project, editor, showDialog, hideDialog, parentSceneId, modifyStateCallback, options = {}) {
    const store = this.getStore();

    const scene = editor.scene;
    const shouldCheckCompile = editor.sceneModified;

    const abortController = new AbortController();
    const signal = abortController.signal;

    // Ensure the user is authenticated before continuing.
    if (!this.isAuthenticated()) {
      await this.loginWithPopup();
    }

    try {
      await editor.validateScene();
    } catch (error) {
      showDialog(ErrorDialog, {
        title: "Validation Error",
        message: `Some nodes in the scene contain properties with invalid values. Please review the hierarchy for affected nodes and refer to the error notification icons for detailed information on the issues.`,
        error,
        confirmLabel: "Fix Issues"
      });
      error.showedDialog = true;
      throw error;
    }

    showDialog(ProgressDialog, {
      title: "Health Check",
      message:
        "We're evaluating your scene to ensure it's optimized for deployment. After the check, you'll receive a detailed report with findings and suggestions for improvement.",
      cancelable: true,
      onCancel: () => abortController.abort()
    });

    try {
      await editor.preprocessNode();
    } catch (error) {
      showDialog(ErrorDialog, {
        title: "Error",
        message: error.message,
        error,
        confirmLabel: "Fix Issues"
      });
      error.showedDialog = true;
      throw error;
    }

    // Take a screenshot of the scene from the current camera position to use as the thumbnail
    const screenshotBlob = await editor.takeScreenshot();
    const screenshotUrl = URL.createObjectURL(screenshotBlob);

    if (signal.aborted) {
      const error = new Error("Publish project aborted");
      error.aborted = true;
      throw error;
    }

    const userInfo = this.getUserInfo();

    // Gather all the info needed to display the publish dialog
    let { name, creatorAttribution, allowRemixing, allowPromotion } = scene.metadata;

    name = (project?.scene && project?.scene?.name) || name || editor?.scene?.name;

    if (project?.scene) {
      allowPromotion = project?.scene?.allow_promotion;
      allowRemixing = project?.scene?.allow_remixing;
      creatorAttribution = project?.scene?.attributions?.creator || store?.authUser?.name || "";
    } else if ((!creatorAttribution || creatorAttribution.length === 0) && userInfo && userInfo.creatorAttribution) {
      creatorAttribution = userInfo.creatorAttribution;
    }

    try {
      // Step 2 Health Check Report
      const healthCheckResult = await editor.exportScene(abortController.signal, { scores: true });

      if (signal.aborted) {
        const error = new Error("Health check aborted");
        showDialog(ErrorDialog, {
          title: "Health Check Stopped",
          message:
            "The health check process was abruptly stopped. If you intentionally canceled the check, you can now continue editing your scene. If this was unexpected, please run the health check again to ensure everything is properly evaluated.",
          confirmLabel: "Understood"
        });

        error.showedDialog = true;
        throw error;
      }

      const glbBlob = healthCheckResult.glbBlob;
      const scores = healthCheckResult.scores;

      const performanceCheckResult = await new Promise(resolve => {
        showDialog(PerformanceCheckDialog, {
          scores,
          onCancel: () => resolve(false),
          onConfirm: () => resolve(true),
          cancelLabel: "Fix Issues",
          confirmLabel: "Continue"
        });
      });

      if (!performanceCheckResult) {
        const error = new Error("Publish project canceled");
        error.aborted = true;
        throw error;
      }

      // Fetch site settings and categories data before showing publish dialog
      showDialog(ProgressDialog, {
        title: "Loading",
        message: "Preparing the deployment  ...",
        cancelable: false
      });

      // Fetch site settings and categories in parallel
      let defaultSiteSettings = null;
      let categoriesData = { data: [], enabled: false };

      try {
        [defaultSiteSettings, categoriesData] = await Promise.all([
          this.getPlatformSiteSettings().catch(error => {
            console.error("Error fetching site settings:", error);
            return null;
          }),
          this.getPublishingCategories("application").catch(error => {
            console.error("Error fetching categories:", error);
            return { data: [], enabled: false };
          })
        ]);
      } catch (error) {
        console.error("Error fetching publish dialog data:", error);
      }

      // Step 3 Publish Dialog
      const publishParams = await new Promise(resolve => {
        showDialog(PublishDialog, {
          initialName: editor.scene.name,
          initialDescription: editor.scene.metadata.description,
          initialTags: editor.scene.metadata.tags,
          initialCategory: editor.scene.metadata.category,
          initialAuthor: editor.scene.metadata.author,
          thumbnailUrl: screenshotUrl,
          platformSiteSettings: defaultSiteSettings,
          categoriesData,
          // contentAttributions,
          initialSceneParams: {
            name,
            creatorAttribution: creatorAttribution || "",
            allowRemixing: typeof allowRemixing !== "undefined" ? allowRemixing : false,
            allowPromotion: typeof allowPromotion !== "undefined" ? allowPromotion : false
          },
          onCancel: () => resolve(null),
          onConfirm: data => resolve(data)
        });
      });

      // // User clicked cancel
      if (!publishParams) {
        URL.revokeObjectURL(screenshotUrl);
        hideDialog();
        const error = new Error("Publish project aborted");
        error.aborted = true;
        throw error;
      }

      const publishProjectName = publishParams?.name || editor.scene.name;

      // Keep draft branch title aligned with the publish title selection.
      editor.setProperty(editor.scene, "name", publishProjectName, false);

      // Update the scene with the metadata from the publishDialog
      scene.setMetadata({
        name: publishProjectName,
        description: publishParams.description,
        category: publishParams.selectedCategory,
        tags: publishParams.tags,
        author: publishParams.author
      });

      showDialog(SaveNewProjectDialog, {
        message: "Compiling Scene",
        initialName: publishProjectName || project?.name || editor.scene.name,
        loading: true,
        thumbnailUrl: screenshotUrl
      });

      if (shouldCheckCompile) {
        const hasErrors = await editor.checkCompile();
        if (hasErrors) {
          const error = new Error(
            "Errors have been detected in your scripts. Check the console panel to see detailed messages about the issues."
          );
          error.showedDialog = true;
          showDialog(ErrorDialog, {
            title: "Compilation Error",
            confirmLabel: "Show Console",
            message: error.message,
            onConfirm: () => {
              editor.emit("show-console-panel");
              hideDialog();
            }
          });
          throw error;
        }
      }

      if (!project) {
        project = await this.createProject(editor.scene, parentSceneId, screenshotBlob, abortController.signal);
        editor.sceneModified = false;

        modifyStateCallback(project);
      }

      // Nest uses the stable seven-character Project key in Project/VCS and
      // Build URLs. Its internal ULID (`id`) is not a route identifier.
      const projectKey = project?.project_key;
      let savedVersion;
      // Always save the project to persist metadata changes (author, description, tags, category)
      // even if the scene itself hasn't been modified
      if (projectKey) {
        const opts = {
          projectName: publishProjectName,
          message: "Publish Checkpoint",
          userId: project.created_by_identity_user_id,
          lastPublishedAt: project.last_published_version_id
        };
        let saveResponse;
        try {
          saveResponse = await this.saveProject(projectKey, editor, signal, opts);
        } catch (error) {
          if (error.status === 402) {
            editor.handle402Error("storage");
            error.showedDialog = true;
          }
          throw error;
        }

        // Staged draft completion already returns the canonical immutable
        // Project Version object. It is both the Build provenance and the
        // opaque .mud descriptor reference; do not parse or rewrite it.
        savedVersion = saveResponse;
        const newBranch = savedVersion?.branch_id;
        const newCommitId = savedVersion?.id;

        // Emit event with version info so UI can update to show the new commit
        if (newBranch && newCommitId) {
          this.emit("project-saved-with-version", {
            branchId: newBranch,
            commitId: newCommitId
          });
        }

        if (signal.aborted) {
          const error = new Error("Publish project aborted");
          error.aborted = true;
          throw error;
        }
      }

      if (!savedVersion?.id || !savedVersion?.content_resource_id) {
        throw new Error("Project save did not return an immutable version and descriptor resource.");
      }

      // Step 5 Export Scene
      showDialog(SaveNewProjectDialog, {
        message: "Publishing Scene",
        initialName: project.name,
        loading: true,
        thumbnailUrl: screenshotUrl
      });

      const size = glbBlob.size / 1024 / 1024;
      const maxSize = this.maxUploadSize;
      if (size > maxSize) {
        throw new Error(`Scene is too large (${size.toFixed(2)}MB) to publish. Maximum size is ${maxSize}MB.`);
      }

      showDialog(SaveNewProjectDialog, {
        title: "Publishing Scene",
        thumbnailUrl: screenshotUrl,
        message: "Uploading thumbnail...",
        loading: true
      });

      let buildWrite;
      try {
        buildWrite = await beginApplicationBuild({
          dashboardApiClient,
          projectKey,
          command: buildApplicationBuildPayload({
            sourceProjectVersionId: savedVersion.id,
            categoryId: publishParams.selectedCategoryId,
            name: publishProjectName,
            description: publishParams.description,
            activations: buildApplicationActivations(options.version_type)
          }),
          idempotencyKey: `application-build-${crypto.randomUUID()}`,
          signal
        });

        const screenshotId = await uploadDelegatedAsset({
          uploadFile,
          file: new File([screenshotBlob], "thumbnail.png", { type: "image/png" }),
          folder: `assets360/${configs.APP_NAME || configs.REACT_APP_NAME || "onland"}/projects`,
          assetType: "project",
          slot: findDelegatedAssetSlot(buildWrite, "build_thumbnail"),
          signal
        });

        if (signal.aborted) {
          const error = new Error("Publish project aborted");
          error.aborted = true;
          throw error;
        }

        const glbId = await uploadDelegatedAsset({
          uploadFile,
          file: new File([glbBlob], "scene.glb", { type: "model/gltf-binary" }),
          folder: `assets360/${configs.APP_NAME || configs.REACT_APP_NAME || "onland"}/projects`,
          assetType: "project_build",
          slot: findDelegatedAssetSlot(buildWrite, "build_glb"),
          signal
        });

        if (signal.aborted) {
          const error = new Error("Publish project aborted");
          error.aborted = true;
          throw error;
        }

        const build = await completeApplicationBuild({
          dashboardApiClient,
          projectKey,
          operationId: buildWrite.operation_id,
          glbResourceId: glbId,
          thumbnailResourceId: screenshotId,
          signal
        });
        if (signal.aborted) {
          const error = new Error("Publish project aborted");
          error.aborted = true;
          throw error;
        }

        project = reconcileProjectDefaultRooms(project, build.deployments);
        const activationEnvironments = Array.isArray(build.deployments)
          ? build.deployments.map(deployment => deployment.environmentKey)
          : [];
        const productionStatus = activationEnvironments.includes("production") ? { status: "success" } : null;
        const draftStatus = activationEnvironments.includes("development") ? { status: "success" } : null;
        const productionUrl = defaultRoomUrlForEnvironment(build.deployments, "production");
        const draftUrl = defaultRoomUrlForEnvironment(build.deployments, "development");

        showDialog(PublishedSceneDialog, {
          sceneName: publishProjectName,
          screenshotUrl,
          status: "success",
          versionType: options.version_type,
          productionUrl,
          draftUrl,
          productionStatus,
          draftStatus,
          onConfirm: undefined,
          onCancel: () => {
            hideDialog();
            this.emit("project-published");
            if (screenshotUrl) {
              URL.revokeObjectURL(screenshotUrl);
            }
          }
        });
      } catch (error) {
        if (buildWrite?.operation_id) {
          await cancelApplicationBuild({ dashboardApiClient, projectKey, operationId: buildWrite.operation_id }).catch(
            () => undefined
          );
        }
        throw error;
      }
    } finally {
      //
    }

    return project;
  }

  setUserInfo(userInfo) {
    localStorage.setItem("spoke-user-info", JSON.stringify(userInfo));
  }

  getUserInfo() {
    return JSON.parse(localStorage.getItem("spoke-user-info"));
  }

  async fetch(url, options) {
    try {
      const res = await fetch(url, options);

      if (res.ok) {
        return res;
      }

      let errorDetail = "";
      try {
        const json = await res.clone().json();
        if (json && json.error) {
          errorDetail = typeof json.error === "string" ? json.error : JSON.stringify(json.error);
        }
      } catch (e) {
        // Ignore JSON parsing errors
      }

      const statusText = res.statusText || "Unknown Error. Possibly a CORS error.";
      const message = errorDetail
        ? `${errorDetail}`
        : `Network Error: ${res.status || "Unknown Status."} ${statusText}`;

      const err = new Error(message);
      err.response = res;
      err.status = res.status;
      throw err;
    } catch (error) {
      if (error.message === "Failed to fetch") {
        error.message += " (Possibly a CORS error)";
      }
      throw new RethrownError(`Failed to fetch "${url}"`, error);
    }
  }

  async getScenes(params) {
    return this.scenesService.getScenes(this.fetch, params);
  }

  async getAllScenes(params) {
    return this.scenesService.getAllScenes(this.fetch, params);
  }

  async adminDeleteMetaverseScene(scene) {
    const metaverseToken = this.metaverseTokenProvider?.();
    return this.scenesService.adminDeleteScene(this.fetch, { id: scene.id, metaverseToken });
  }

  async getAllRooms(params) {
    return this.scenesService.getAllRooms(this.fetch, params);
  }

  async adminDeleteMetaverseRoom(roomId) {
    return this.scenesService.adminDeleteRoom(this.fetch, roomId);
  }

  async assignSceneToGroups(sceneId, groupIds, groupPrivacy = null) {
    return this.scenesService.assignSceneToGroups(this.fetch, sceneId, groupIds, groupPrivacy);
  }

  async removeSceneFromGroup(sceneId, groupId) {
    return this.scenesService.removeSceneFromGroup(this.fetch, sceneId, groupId);
  }

  // Workspace Assignment Functions
  async getAdminWorkspaces(filters = {}) {
    const token = await getAuth0Token();
    const params = new URLSearchParams();

    if (filters.search) {
      params.append("search", filters.search);
    }
    if (filters.page) {
      params.append("page", filters.page);
    }
    if (filters.perPage) {
      params.append("perPage", filters.perPage);
    }
    if (filters.sort_by) {
      params.append("sort_by", filters.sort_by);
    }
    if (filters.sort_order) {
      params.append("sort_order", filters.sort_order);
    }

    const url = `${configs.CUSTOM_API_URL}/admin/workspaces${params.toString() ? "?" + params.toString() : ""}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        throw new Error(`Network Error: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error fetching admin workspaces:", error);
      throw error;
    }
  }

  async assignSceneToWorkspaces(sceneId, workspaceIds, permissions = null) {
    return this.scenesService.assignSceneToWorkspaces(this.fetch, sceneId, workspaceIds, permissions);
  }

  async removeSceneFromWorkspace(sceneId, workspaceId) {
    return this.scenesService.removeSceneFromWorkspace(this.fetch, sceneId, workspaceId);
  }

  async getMetaverseScene(id) {
    return this.scenesService.getScene(this.fetch, id);
  }

  async deleteMetaverseScene(scene) {
    return this.scenesService.deleteScene(this.fetch, {
      id: scene.id,
      metaverseToken: this.getToken()
    });
  }

  async updateMetaverseRoomVisibility({ roomId, isPublic }) {
    try {
      const response = await dashboardApiClient.putJson(`metaverse-rooms/${roomId}/visibility`, {
        is_public: isPublic
      });
      return response.data || response;
    } catch (error) {
      console.error("Error updating room visibility:", error);
      throw error;
    }
  }

  async makeMetaverseRoomPublic(roomId) {
    return this.scenesService.makeRoomPublic(this.fetch, roomId);
  }

  async makeMetaverseRoomPrivate(roomId) {
    return this.scenesService.makeRoomPrivate(this.fetch, roomId);
  }

  async getPublicMetaverseRooms() {
    return this.scenesService.getPublicRooms(this.fetch);
  }

  async getRooms() {
    return this.scenesService.getRooms(this.fetch);
  }

  async getGroupScenes(groupId, params) {
    return this.scenesService.getGroupScenes(this.fetch, groupId, params);
  }

  async getSubscriptions(queryParams) {
    return this.subscriptionsService.getSubscriptions(this.fetch, {
      queryParams
    });
  }

  async getSubscriptionStatus(queryParams) {
    return this.subscriptionsService.getSubscriptionStatus(this.fetch, {
      queryParams
    });
  }

  async getEffectiveEntitlements() {
    return this.subscriptionsService.getEffectiveEntitlements(this.fetch);
  }

  async validateSession(sessionId) {
    return this.subscriptionsService.validateSession(this.fetch, {
      sessionId
    });
  }

  async getCharges(root) {
    return this.subscriptionsService.getCharges(this.fetch, { root });
  }

  async getSubscriptionCharges(id) {
    return this.subscriptionsService.getSubscriptionCharges(this.fetch, { id });
  }

  async cancelSubscription(id) {
    return this.subscriptionsService.cancelSubscription(this.fetch, { id });
  }

  async resumeSubscription(id) {
    return this.subscriptionsService.resumeSubscription(this.fetch, { id });
  }

  getWorkspaceIdSafe() {
    try {
      return this.getWorkspaceId();
    } catch (e) {
      return null;
    }
  }

  async getFolders(root) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.getFolders(this.fetch, { root, workspaceId });
  }

  async createFolder(body) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.createFolder(this.fetch, { body, workspaceId });
  }

  async updateFolder(folder, body) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.updateFolder(this.fetch, { folder, body, workspaceId });
  }

  async deleteFolder(folder) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.deleteFolder(this.fetch, { folder, workspaceId });
  }

  async recoverFolder(folder) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.recoverFolder(this.fetch, { folder, workspaceId });
  }

  async addToFolder({ folder, project }) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.addToFolder(this.fetch, { folder, project, workspaceId });
  }

  async addToFolderMultiple({ folder, projects, type = "project" }) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.addToFolderMultiple(this.fetch, {
      folder,
      projects,
      type, // Pass type
      workspaceId
    });
  }

  async addProjectToMultipleFolders({ folders, projects, type = "project" }) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.addProjectToMultipleFolders(this.fetch, {
      folders,
      projects,
      type, // Pass type
      workspaceId
    });
  }

  // Scene-specific wrappers for clarity
  async addSceneToMultipleFolders({ folders, scenes }) {
    return this.addProjectToMultipleFolders({
      folders,
      projects: scenes,
      type: "scene"
    });
  }

  async addSceneToFolder({ folder, scene }) {
    return this.addToFolderMultiple({
      folder,
      projects: [scene],
      type: "scene"
    });
  }

  async removeOfFolder({ projects, folder }) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.removeOfFolder(this.fetch, {
      projects,
      folder,
      workspaceId
    });
  }

  async getFolderProjects(folder) {
    const workspaceId = this.getWorkspaceIdSafe();
    return this.folderService.getFolderProjects(this.fetch, { folder, workspaceId });
  }

  async folderProjectUnsync({ folder, projects }) {
    return this.removeOfFolder({ folder, projects });
  }

  async fetchMetaverseAuthToken() {
    const url = configs["CUSTOM_API_URL"] + "/metaverse/token";
    const auth0token = await getAuth0Token();

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth0token}`,
        Accept: "application/json"
      }
    }).catch(() => {
      return null;
    });

    if (!response.ok) {
      return null;
    }

    const data = await response.json();

    return data;
  }

  async syncSceneGroup(project, groups) {
    const store = this.getStore();
    const metaverseData = await this.fetchMetaverseAuthToken();

    const token = await getAuth0Token();

    // Map Recent Scenes in DB
    await this.getOwnedUserScene(token, metaverseData);

    // Sync Groups with Scene
    await this.mapSceneUsersGroup(token, project, groups);

    await this.findSceneSetUser(token, project, store.authUser);
  }

  getIsAdmin() {
    const store = this.getStore();
    const userPermissions = store?.authUser?.permissions;
    return Array.isArray(userPermissions) && userPermissions.includes(configs.ADMIN_PERMISSION);
  }

  hasPermission(permission) {
    if (!permission) {
      return false;
    }

    const normalizedPermission = permission.toLowerCase();

    if (isCanonicalWorkspacePermission(normalizedPermission)) {
      return workspaceHasPermission(this.getWorkspace(), normalizedPermission);
    }

    // Regular user permissions
    const permissions = this.getUserPermissions();

    if (!permissions.length) {
      return false;
    }

    return permissions.some(userPermission => userPermission.toLowerCase() === normalizedPermission);
  }

  hasAnyPermission(requiredPermissions) {
    if (!requiredPermissions) {
      return false;
    }

    const permissionsList = Array.isArray(requiredPermissions) ? requiredPermissions : [requiredPermissions];

    if (!permissionsList.length) {
      return false;
    }

    return permissionsList.some(requiredPermission => this.hasPermission(requiredPermission));
  }

  getUserPermissions() {
    const store = this.getStore();
    const storedPermissions = store?.authUser?.permissions;

    if (!Array.isArray(storedPermissions)) {
      return [];
    }

    return storedPermissions;
  }

  async refreshUserPermissions() {
    try {
      const token = await getAuth0Token();
      if (!token) {
        console.warn("No auth token available for permission refresh");
        return false;
      }

      const userData = await this.getAuth0User(token, true);
      if (userData) {
        console.log("User permissions refreshed successfully");
        return true;
      }

      console.warn("Failed to refresh user permissions");
      return false;
    } catch (err) {
      console.error("Error refreshing user permissions:", err);
      return false;
    }
  }

  async createRoom(scene) {
    const { scene_id: sceneId } = scene;
    const thumbnail = scene.screenshot_url || scene.images_preview_url;

    const body = {
      name: scene.name,
      sceneId,
      thumbnail
    };

    try {
      const response = await dashboardApiClient.postJson("metaverse-rooms", body, true, {
        headers: getTenantHostHeader()
      });
      return response.data || response;
    } catch (error) {
      console.error("Error creating room:", error);
      return null;
    }
  }

  async deleteRoom(roomId) {
    return this.scenesService.deleteRoom(this.fetch, roomId);
  }

  async storeMetaverseScene(project) {
    try {
      const token = await getAuth0Token();
      const url = `${configs.CUSTOM_API_URL}/metaverse-scenes`;

      const body = {
        id: project.scene.scene_id,
        name: project.scene.name,
        description: project.scene.description,
        images_preview_url: project.scene.screenshot_url,
        allow_remixing: project.scene.allow_remixing,
        project_id: project.project_id,
        type: project.scene.type,
        url: project.scene.url,
        attributions: project.scene.attributions,
        privacy: "private",
        published: false
      };

      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async executePersistableCommand({ id, cmd, params, env = "dev" }) {
    const url = configs["PERSISTENCE_API_URL"] + "/persistable/" + id;

    try {
      const response = await fetch(url, {
        method: "POST",
        body: JSON.stringify({
          env,
          cmd,
          params
        }),
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getAuth0Token()}`,
          Accept: "application/json",
          "X-API-KEY": configs.PERSISTENCE_API_KEY
        }
      });

      if (!response.ok) {
        return null;
      }

      const data = await response.json();

      return data;
    } catch (e) {
      console.error(e);
      return null;
    }
  }

  async registerPersistables(scene, sceneId) {
    const sceneIdForRegistration = sceneId;
    const persistableNodeUuids = [];

    scene.traverse(object => {
      if (object.constructor === PersistableNode || object.type === PersistableNode.nodeName) {
        persistableNodeUuids.push(object.uuid);
      }
    });

    try {
      await this.executePersistableCommand({
        id: "register",
        cmd: "register",
        params: {
          sceneId: sceneIdForRegistration,
          persistablesIds: persistableNodeUuids // This will be an empty array if none are found
        }
      });
    } catch (regError) {
      console.error("Error calling executePersistableCommand for 'register':", regError);
    }
  }

  async saveOnboardingData(data) {
    const preferences = {
      ...data,
      onboarding_completed: true
    };

    // Save locally and sync (debounced)
    userPreferences.save(preferences);

    // Return mock success as sync handles itself
    return { success: true, preferences };
  }

  async updatePreferences(data) {
    const url = configs["CUSTOM_API_URL"] + "/preferences";
    const auth0token = await getAuth0Token();
    userPreferences.save(data);

    const response = await fetch(url, {
      method: "PUT",
      body: JSON.stringify(preferencesForRemoteSync(data)),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth0token}`,
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      return null;
    }

    const res = await response.json();

    return res;
  }

  async subscribeToNewsletter(email, name, newsletter) {
    const tags = newsletter ? "Newsletter,Login" : "Login";
    const data = { email, name, tags };

    const url = configs["CUSTOM_API_URL"] + "/subscribe";

    fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(data)
    });
  }

  async getUserProfile(id) {
    const url = configs["CUSTOM_API_URL"] + "/users/" + id + "/profile";

    try {
      const auth0token = await getAuth0Token();

      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${auth0token}`,
          Accept: "application/json"
        }
      });

      if (!response.ok) {
        return null;
      }

      const res = await response.json();

      return res;
    } catch (err) {
      return null;
    }
  }

  async updateUserProfile(userId, data) {
    const url = configs["CUSTOM_API_URL"] + "/users/" + userId + "/profile";

    const auth0token = await getAuth0Token();

    const response = await fetch(url, {
      method: "PUT",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth0token}`,
        Accept: "application/json"
      }
    }).catch(() => {
      return null;
    });

    if (!response.ok) {
      return null;
    }

    const res = await response.json();

    return res;
  }

  async setCurrentWorkspace(workspaceId) {
    const url = configs["CUSTOM_API_URL"] + "/workspaces/current";

    const auth0token = await getAuth0Token();

    const response = await fetch(url, {
      method: "PUT",
      body: JSON.stringify({ workspace_id: workspaceId }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth0token}`,
        Accept: "application/json"
      }
    }).catch(() => {
      return null;
    });

    if (!response.ok) {
      return null;
    }

    const res = await response.json();

    return res;
  }

  async leaveWorkspace(workspaceId) {
    const url = configs["CUSTOM_API_URL"] + "/workspaces/" + workspaceId + "/leave";

    const auth0token = await getAuth0Token();

    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth0token}`,
        Accept: "application/json"
      }
    }).catch(() => {
      return null;
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: "Unknown error" }));
      throw new Error(errorData.message || "Failed to leave workspace");
    }

    const res = await response.json();

    return res;
  }

  credentialsAccountId() {
    const token = this.getToken();
    if (token) {
      return jwtDecode(token).sub;
    }
  }

  avatarId() {
    const store = this.getStore();
    const profile = store.profile || {};

    if (profile.avatarId) {
      return profile.avatarId;
    }

    return null;
  }

  setAvatar(avatarId) {
    const store = this.getStore();
    const profile = store.profile || {};

    profile.avatarId = avatarId;

    localStorage.setItem(LOCAL_STORE_KEY, JSON.stringify({ ...store, profile }));
  }

  exportProject(project, sourceBranchId = null) {
    return this.projectsService.exportProject(project, sourceBranchId);
  }

  importProject(blob) {
    return this.projectsService.importProject(blob);
  }

  adminGetProjects(params, options) {
    return this.projectsService.adminGetProjects(params, options);
  }

  adminDeleteProject(projectId) {
    return this.projectsService.adminDeleteProject(projectId);
  }

  getTemplates(queryParams = {}, optionsOrSignal) {
    let signal = optionsOrSignal;
    let workspaceId = null;

    // Support passing an options object as the second argument
    // Check if it's a plain object and has workspaceId or signal property, or implies options
    // Use a simple check: if it's not an AbortSignal instance (if supported) or check for specific props
    if (optionsOrSignal && typeof optionsOrSignal === "object" && optionsOrSignal.constructor === Object) {
      signal = optionsOrSignal.signal;
      workspaceId = optionsOrSignal.workspaceId;
    }

    return this.withPhoenixAuth(() =>
      this.templatesService.getTemplates(this.fetch, {
        params: queryParams,
        signal,
        workspaceId
      })
    );
  }

  getTemplate(templateId, workspaceId = null, signal) {
    return this.withPhoenixAuth(() => this.templatesService.getTemplate(templateId, workspaceId, signal));
  }

  getPublicEvents(queryParams = {}, signal) {
    return this.eventService.getPublicEvents({ queryParams, signal });
  }

  createTemplate(project, name, description, options) {
    return this.withPhoenixAuth(() => this.templatesService.createTemplate(project, name, description, options));
  }

  updateTemplate(template, workspaceId) {
    return this.withPhoenixAuth(() => this.templatesService.updateTemplate(template, workspaceId));
  }

  deleteTemplate(template, workspaceId) {
    return this.withPhoenixAuth(() => this.templatesService.deleteTemplate(template, workspaceId));
  }

  synchronizeTemplate(template, workspaceId, sourceBranchId = null) {
    return this.withPhoenixAuth(() => this.templatesService.synchronizeTemplate(template, workspaceId, sourceBranchId));
  }

  getAuthUser() {
    const store = this.getStore();

    return store?.authUser || null;
  }

  async synchronizeProject({ project_id, source_project_id, project_url, name, thumbnail_url, options = {} }) {
    return this.withPhoenixAuth(async () => {
      const response = await fetch(configs.PERSISTENCE_API_URL + "/projects/" + project_id + "/synchronize", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${await getAuth0Token()}`,
          "X-METAVERSE-TOKEN": this.getToken(),
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          source_project_id: source_project_id,
          project_url: project_url,
          name: name || options.name,
          thumbnail_url: thumbnail_url,
          options: options
        })
      });

      this.emit("project-saved");

      if (response.status === 402) {
        this.emit("402-error");
        throw new Error("Storage limit reached");
      }

      const json = await response.json();

      return json;
    });
  }

  async reportBug(bug) {
    const url = configs["CUSTOM_API_URL"] + "/bugs";

    const auth0token = await getAuth0Token();

    const { screenshot } = bug;

    let screenshotAssetResourceId = null;

    if (screenshot) {
      const upload = await uploadFile({
        file: screenshot,
        folder: "bug-reports",
        asset_type: "bug_report_screenshot",
        hidden: 1,
        ACL: "private",
        useWorkspaceAssets: true
      });

      screenshotAssetResourceId = upload.uuid || upload.id || null;
    }

    const response = await fetch(url, {
      method: "POST",
      body: JSON.stringify({
        subject: bug.subject,
        description: bug.description,
        screenshot_asset_resource_id: screenshotAssetResourceId,
        severity: bug.severity,
        deviceAffected: bug.devices
      }),
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth0token}`,
        Accept: "application/json"
      }
    }).catch(() => {
      return null;
    });

    if (!response || !response.ok) {
      return null;
    }

    const data = await response.json();

    return data;
  }

  async createEvent(event) {
    return this.eventService.createEvent(event);
  }

  async updateEvent(eventId, event) {
    return this.eventService.updateEvent(eventId, event);
  }

  async toggleFeatured(slug) {
    return this.eventService.toggleFeatured(slug);
  }

  async getEvents() {
    return this.eventService.getEvents();
  }

  async getMaasEvents(query = "", page = 1, perPage = 50, signal = null) {
    return this.eventService.getMaasEvents(query, page, perPage, signal);
  }

  async getTrashedEvents() {
    return this.eventService.getTrashedEvents();
  }

  async getEventMetrics(...args) {
    return this.eventService.getEventMetrics(...args);
  }

  async deleteEvent(eventId) {
    return this.eventService.deleteEvent(eventId);
  }

  async restoreEvent(slug) {
    return this.eventService.restoreEvent(slug);
  }

  async forceDeleteEvent(slug) {
    return this.eventService.forceDeleteEvent(slug);
  }

  async cloneEvent(slug, cloneOptions) {
    return this.eventService.cloneEvent(slug, cloneOptions);
  }

  async getEventBySlug(slug) {
    return this.eventService.getEventBySlug(slug);
  }

  async submitEventFeedback(slug, feedback) {
    return this.eventService.submitEventFeedback(slug, feedback);
  }

  async getEventFeedback(slug) {
    return this.eventService.getEventFeedback(slug);
  }

  async verifyEventCheckin({ slug, token }) {
    return this.eventService.verifyCheckin({ slug, token });
  }

  async redeemEventCheckin({ slug, token }) {
    return this.eventService.redeemCheckin({ slug, token });
  }

  async getSystemMetrics(startDate, endDate, signal, MaasAnalytics = false) {
    const url =
      configs["CUSTOM_API_URL"] +
      "/analytics?start_date=" +
      startDate +
      "&end_date=" +
      endDate +
      "&system=" +
      MaasAnalytics;

    const auth0token = await getAuth0Token();

    const response = await fetch(url, {
      signal: signal,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${auth0token}`
      }
    });

    if (!response.ok) {
      throw new Error("Failed to fetch system metrics");
    }

    const json = await response.json();

    return json.data;
  }

  async getSceneMetrics(...args) {
    return this.scenesService.getSceneMetrics(this.fetch, ...args);
  }

  async previewEventEmail(slug, { type, variant, email } = {}) {
    return this.eventService.previewEventEmail(slug, { type, variant, email });
  }

  async holdReservation(body) {
    return this.eventService.holdReservation(body);
  }

  async claimReservation(body) {
    return this.eventService.claimReservation(body);
  }

  async claimInvitation({ token, slug }) {
    return this.eventService.claimInvitation({ token, slug });
  }

  /**
   * Claim a user invitation (for roles, groups, subscription templates)
   * @param {string} token - The invitation token
   * @returns {Promise<object>} - The claim result with applied effects
   */
  async acceptWorkspaceInvitation(token) {
    const url = configs["CUSTOM_API_URL"] + "/workspaces/invitations/accept";
    const headers = {
      "Content-Type": "application/json"
    };
    const auth0Token = await getAuth0Token();
    if (auth0Token) {
      headers.Authorization = `Bearer ${auth0Token}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ token })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Failed to accept workspace invitation");
    }

    return await response.json();
  }

  async claimUserInvitation(token) {
    const url = configs["CUSTOM_API_URL"] + "/user-invitations/claim";
    const headers = {
      "Content-Type": "application/json"
    };
    const auth0Token = await getAuth0Token();
    if (auth0Token) {
      headers.Authorization = `Bearer ${auth0Token}`;
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify({ token })
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Failed to claim invitation");
    }

    return response.json();
  }

  /**
   * Get user invitation details by token (for preview before login)
   * @param {string} token - The invitation token
   * @returns {Promise<object>} - The invitation details
   */
  async getUserInvitationDetails(token) {
    const url = configs["CUSTOM_API_URL"] + `/user-invitations/claim/${token}`;
    const response = await fetch(url);

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.message || "Invitation not found");
    }

    return response.json();
  }

  async getPlatformSiteSettings() {
    return this.siteSettingsService.getDefault(this.fetch);
  }

  async createPlatformSiteSettings(data) {
    return this.siteSettingsService.create(this.fetch, data);
  }

  async getMySiteSettings() {
    return this.siteSettingsService.getMine(this.fetch);
  }

  async getSiteSettings(id) {
    return this.siteSettingsService.findById(this.fetch, id);
  }

  async updateSiteSettings(id, data) {
    return this.siteSettingsService.update(this.fetch, id, data);
  }

  async getPublishingCategories(resourceType) {
    return this.publishingCategoriesService.getCategories(resourceType);
  }
}
